import {
  ConstraintValueItemType,
  CustomInputFacets,
  GeneralLocationFacets,
  LocationConstraintValueItemType,
  SegpSupportedFacets,
  SpecificLocationFacets,
  ValueItemTypeSupportedOperator,
} from './const';
import { getAvailableNamespaces } from './SegmentSelector/SegmentSelector';
import { FACETS } from '@/constants/facets';
import { Constraint } from '@/lib/PlannerDTOs';

export enum SegmentNameSpace {
  SHARED = 'shared',
}

// Constraint targets whose SegP serialization prefix differs from the target itself.
// The Mex Merchant facet (mem) is a GrabX constraint target, but its SegP segments
// are stored under the MEX entity type (mex). This intentional decoupling means:
// - detectValueItemType recognizes values by their serialized prefix (mex), not the
//   constraint target (mem), so mem is in SegpSupportedFacets for UI gating only.
// - getConstraintPayloadValue uses this map to serialize mem → mex.segp.mexmerchant.*
// - getConstraintDisplayValue/getConstraintNamespace parse from the value string and
//   work correctly because getAvailableNamespaces returns ['mexmerchant'] for mem.
export const FacetToSegpPrefix: Record<string, string> = {
  [FACETS.MexMerchant]: FACETS.Merchant, // mem → mex
};

export const getValueItemTypeOptions = (constraint?: Constraint) => {
  if (!constraint?.target) return [];

  // location item type
  let locationOptions = Object.keys(LocationConstraintValueItemType).map(
    (key) => ({
      label: LocationConstraintValueItemType[key].label,
      value: LocationConstraintValueItemType[key].value,
    }),
  );

  if (SpecificLocationFacets.includes(constraint.target)) {
    return locationOptions;
  }

  // group-related item type
  let groupOptions = Object.keys(ConstraintValueItemType).map((key) => ({
    label: ConstraintValueItemType[key].label,
    value: ConstraintValueItemType[key].value,
  }));

  if (!SegpSupportedFacets.includes(constraint.target)) {
    groupOptions = groupOptions.filter(
      (i) => i.value !== ConstraintValueItemType.SEGP.value,
    );
  }

  if (GeneralLocationFacets.includes(constraint.target)) {
    groupOptions = [...groupOptions, ...locationOptions];
  }

  return groupOptions;
};

export const detectValueItemType = (constraint?: Constraint) => {
  if (!constraint?.value) {
    return null;
  }
  if (CustomInputFacets.includes(constraint.target)) {
    return null;
  }
  if (!ValueItemTypeSupportedOperator.includes(constraint.op)) {
    return null;
  }
  if (constraint.value.startsWith('[') && constraint.value.endsWith(']')) {
    return ConstraintValueItemType.INLINE.value;
  }
  const groupList = constraint.value.split(',');
  if (groupList.length === 0) {
    return null;
  }
  if (
    groupList.every((group) => {
      const splittedValue = group.split('.');
      return (
        splittedValue.length > 2 &&
        splittedValue[1] === 'segp' &&
        SegpSupportedFacets.includes(splittedValue[0])
      );
    })
  ) {
    return ConstraintValueItemType.SEGP.value;
  }
  return ConstraintValueItemType.STATIC.value;
};

export const getConstraintDisplayValue = (
  constraint?: Constraint,
): string[] => {
  const { value: constraintValue, valueItemType } = constraint;
  if (!constraint?.value) {
    return [];
  }
  if (valueItemType === ConstraintValueItemType.INLINE.value) {
    if (constraintValue.startsWith('[') && constraintValue.endsWith(']')) {
      return constraintValue.slice(1, -1).split(',');
    }
    return [];
  }
  if (valueItemType === ConstraintValueItemType.SEGP.value) {
    const segmentGroups = constraintValue.split(',');
    const segmentNames = segmentGroups.map((segment) => {
      const segmentParts = segment.split('.');
      if (segmentParts.length < 3) {
        return segment;
      }
      if (segmentParts[1] === 'segp') {
        const availableNamespaces = getAvailableNamespaces(constraint);

        if (availableNamespaces.includes(segmentParts[2])) {
          return segmentParts.slice(3).join('.');
        } else {
          return segmentParts.slice(2).join('.');
        }
      }
      return segment;
    });
    return segmentNames;
  }
  if (valueItemType === LocationConstraintValueItemType.GRAB_AREA.value) {
    if (constraintValue.startsWith('[') && constraintValue.endsWith(']')) {
      return constraintValue.slice(1, -1).split(',');
    }
    return [];
  }
  return [];
};

export const getConstraintNamespace = (constraint?: Constraint): string => {
  const { value: constraintValue, valueItemType } = constraint;
  if (!constraint?.value) {
    return '';
  }
  if (valueItemType === ConstraintValueItemType.INLINE.value) return '';

  if (valueItemType === ConstraintValueItemType.SEGP.value) {
    const segmentGroups = constraintValue.split(',');

    if (!segmentGroups.length) return '';

    const segmentParts = segmentGroups[0].split('.');
    if (segmentParts.length < 3) {
      return '';
    }
    if (segmentParts[1] === 'segp') {
      const availableNamespaces = getAvailableNamespaces(constraint);
      const namespace = segmentParts[2];

      if (availableNamespaces.includes(namespace)) {
        return namespace;
      } else {
        return 'shared';
      }
    }
    return '';
  }

  return '';
};

export const getConstraintPayloadValue = ({
  constraintValues,
  valueItemType,
  facet,
}: {
  constraintValues?: string[];
  valueItemType?: string;
  facet?: string;
}): string => {
  if (!constraintValues) {
    return '';
  }

  if (constraintValues.length === 0) {
    return '';
  }

  if (valueItemType === ConstraintValueItemType.SEGP.value) {
    if (facet) {
      const prefix = FacetToSegpPrefix[facet] || facet;
      return constraintValues.map((i) => `${prefix}.segp.${i}`).join(',');
    }
    return '';
  }

  if (valueItemType === ConstraintValueItemType.INLINE.value) {
    return `[${constraintValues.join(',')}]`;
  }

  if (valueItemType === LocationConstraintValueItemType.GRAB_AREA.value) {
    return `[${constraintValues.join(',')}]`;
  }

  return '';
};
